import React from "react";
import ReactSelect from "react-select";
import { useColorModeValue } from "@chakra-ui/react";

// Thin wrapper around react-select that makes the control, menu and options
// color-mode aware. react-select renders its own white surfaces that ignore the
// Chakra theme, so without this every dropdown stays white in dark mode.
// All props are forwarded, so usage is identical to `react-select`.
const AppSelect = ({ styles: userStyles, ...props }) => {
  const bg = useColorModeValue("#ffffff", "#2D3748"); // white / gray.700
  const text = useColorModeValue("#1A202C", "#F7FAFC"); // gray.800 / gray.50
  const border = useColorModeValue("#E2E8F0", "#4A5568"); // gray.200 / gray.600
  const placeholder = useColorModeValue("#718096", "#A0AEC0"); // gray.500/400
  const hover = useColorModeValue("#EDF2F7", "#4A5568"); // gray.100 / gray.600
  const selected = useColorModeValue("#E2E8F0", "#1A202C"); // gray.200 / gray.800

  const styles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: bg,
      borderColor: state.isFocused ? "#FFBF00" : border,
      boxShadow: state.isFocused ? "0 0 0 1px #FFBF00" : "none",
      color: text,
    }),
    singleValue: (base) => ({ ...base, color: text }),
    input: (base) => ({ ...base, color: text }),
    placeholder: (base) => ({ ...base, color: placeholder }),
    menu: (base) => ({ ...base, backgroundColor: bg, zIndex: 30 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? selected
        : state.isFocused
        ? hover
        : bg,
      color: text,
      cursor: "pointer",
    }),
    multiValue: (base) => ({ ...base, backgroundColor: hover }),
    multiValueLabel: (base) => ({ ...base, color: text }),
  };

  // Compose any caller-supplied style functions on top of ours so the dark-mode
  // colors are kept while the caller can still tweak layout (height, etc.).
  // A falsy `styles` (e.g. `cond && customStyles`) is ignored.
  const merged = { ...styles };
  if (userStyles && typeof userStyles === "object") {
    Object.keys(userStyles).forEach((key) => {
      const mine = styles[key];
      const theirs = userStyles[key];
      merged[key] = (base, state) => {
        const withMine = mine ? mine(base, state) : base;
        return theirs ? theirs(withMine, state) : withMine;
      };
    });
  }

  return <ReactSelect styles={merged} {...props} />;
};

export default AppSelect;
